import { z } from 'zod';
import { PlanFeaturesSchema } from './plan-features-schema.js';
// saas-monetization-plans v2 — WU-I.1: provider-neutral Plan CRUD contracts
// (design §2/§8). Mirrors the `Plan` entity/PlanSnapshot fields exactly —
// NO `stripe*`/provider-specific field names anywhere in this file (spec
// "Payment-Provider Decoupling"). `providerPriceRef` is an opaque string, not
// typed as a Stripe price id.
export const PlanIntervalSchema = z.enum(['month', 'year']);
// ── Create ────────────────────────────────────────────────────────────────
export const CreatePlanRequestSchema = z.object({
    name: z.string().trim().min(1, 'name is required').max(120),
    description: z.string().trim().max(2000).nullable().optional(),
    subscriptionAmountCents: z
        .number()
        .int()
        .positive('subscriptionAmountCents must be a positive integer'),
    currency: z.string().regex(/^[A-Za-z]{3}$/, 'currency must be a 3-letter ISO code'),
    interval: PlanIntervalSchema,
    intervalCount: z.number().int().min(1).optional(),
    commissionBasisPoints: z.number().int().min(0).max(10000),
    commissionEnabled: z.boolean().optional(),
    includedVehicles: z.number().int().min(0, 'includedVehicles must be a non-negative integer'),
    overagePriceCents: z.number().int().min(0, 'overagePriceCents must be a non-negative integer'),
    providerPriceRef: z.string().trim().min(1).nullable().optional(),
    features: PlanFeaturesSchema.partial().optional(),
});
// ── Update (PATCH — only provided fields are validated/written) ────────────
export const UpdatePlanRequestSchema = z
    .object({
    name: z.string().trim().min(1, 'name is required').max(120).optional(),
    description: z.string().trim().max(2000).nullable().optional(),
    subscriptionAmountCents: z
        .number()
        .int()
        .positive('subscriptionAmountCents must be a positive integer')
        .optional(),
    currency: z
        .string()
        .regex(/^[A-Za-z]{3}$/, 'currency must be a 3-letter ISO code')
        .optional(),
    interval: PlanIntervalSchema.optional(),
    intervalCount: z.number().int().min(1).optional(),
    commissionBasisPoints: z.number().int().min(0).max(10000).optional(),
    commissionEnabled: z.boolean().optional(),
    includedVehicles: z
        .number()
        .int()
        .min(0, 'includedVehicles must be a non-negative integer')
        .optional(),
    overagePriceCents: z
        .number()
        .int()
        .min(0, 'overagePriceCents must be a non-negative integer')
        .optional(),
    providerPriceRef: z.string().trim().min(1).nullable().optional(),
    features: PlanFeaturesSchema.partial().nullable().optional(),
})
    .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: 'At least one field must be provided',
});
// ── Response ─────────────────────────────────────────────────────────────
export const PlanResponseSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable(),
    active: z.boolean(),
    subscriptionAmountCents: z.number().int(),
    currency: z.string(),
    interval: PlanIntervalSchema,
    intervalCount: z.number().int(),
    commissionBasisPoints: z.number().int(),
    commissionEnabled: z.boolean(),
    includedVehicles: z.number().int(),
    overagePriceCents: z.number().int(),
    providerPriceRef: z.string().nullable(),
    features: PlanFeaturesSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const ListPlansResponseSchema = z.object({
    plans: z.array(PlanResponseSchema),
});
// ── Assign plan to organization ──────────────────────────────────────────
// manual-subscription-billing (design §1/§4) — how the recurring charge is
// collected. `automatic` is the existing Stripe-provisioned path (default).
// `manual` is the new offline/invoice-based path. Defined here (not in
// subscription-schemas.ts) to avoid a circular import — subscription-
// schemas.ts already imports SubscriptionStatusSchema FROM this file.
export const CollectionMethodSchema = z.enum(['automatic', 'manual']);
export const AssignPlanToOrganizationRequestSchema = z.object({
    planId: z.string().uuid(),
    // Required only on the very first assignment for an org (no billing
    // relationship yet) — the use case enforces this conditionally.
    initialAdminEmail: z.string().email().optional(),
    initialAdminName: z.string().trim().min(1).optional(),
    // manual-subscription-billing — optional, defaults to 'automatic'
    // downstream (spec REQ-1.2) so every existing caller that omits it is
    // byte-for-byte unaffected.
    collectionMethod: CollectionMethodSchema.optional(),
});
export const SubscriptionStatusSchema = z.enum([
    'incomplete',
    'active',
    'past_due',
    'suspended',
    'canceled',
]);
export const AssignPlanToOrganizationResponseSchema = z.object({
    organizationId: z.string().uuid(),
    planId: z.string().uuid(),
    platformFeeBasisPoints: z.number().int().min(0).max(10000),
    subscriptionId: z.string().uuid(),
    subscriptionStatus: SubscriptionStatusSchema,
    collectionMethod: CollectionMethodSchema,
    setupUrl: z.string().url().optional(),
});
//# sourceMappingURL=plan-schemas.js.map